import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminTopNav } from "@/components/admin/AdminTopNav";
import { adminGet } from "@/lib/admin-api";

interface PaymentRow {
  id: string;
  externalId: string;
  donorEmail: string;
  invoiceId: string;
  amountUsd: number;
  currency: string;
  gateway: string;
  status: string;
  createdAt: string;
  metadata?: { dpfWishId?: string };
}

interface ReconcileReport {
  date: string;
  stripeIntentsCount: number;
  ledgerRowsCount: number;
  orphans: { stripeOnly: string[]; ledgerOnly: string[] };
}

function PaymentsAdminInner() {
  const [items, setItems] = React.useState<PaymentRow[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<string>("");
  const [recon, setRecon] = React.useState<ReconcileReport | null>(null);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const url = status
        ? `/api/admin/payments/list?status=${status}`
        : `/api/admin/payments/list`;
      const res = await adminGet<{ items: PaymentRow[] }>(url);
      setItems(res.items);
    } catch (err) {
      setError(String(err));
    }
  }, [status]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function onReconcile() {
    setError(null);
    try {
      const res = await adminGet<ReconcileReport>(`/api/admin/payments/reconcile`);
      setRecon(res);
    } catch (err) {
      setError(String(err));
    }
  }

  return (
    <PageShell title="Payments · Admin" status="admin" width="wide">
      <Seo title="Payments admin" description="" path="/admin/payments" noindex />
      <AdminTopNav />

      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <h2 className="font-serif text-xl text-ink">Payments ledger</h2>
        <div className="flex items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-hairline bg-paper px-3 py-2 font-mono text-xs"
          >
            <option value="">All statuses</option>
            <option value="successful">Successful</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
          <button
            type="button"
            onClick={onReconcile}
            className="border border-ink text-ink px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-paper-2"
          >
            Reconcile today
          </button>
        </div>
      </div>

      {recon && (
        <div className="border border-hairline bg-paper-2 p-4 mb-6 font-mono text-xs text-ink-2 leading-relaxed">
          <strong className="text-ink">Reconciliation report — {recon.date}</strong>: Stripe intents {recon.stripeIntentsCount}, ledger rows {recon.ledgerRowsCount}.
          {recon.orphans.stripeOnly.length === 0 && recon.orphans.ledgerOnly.length === 0 ? (
            <span className="text-claude"> Clean — no orphans.</span>
          ) : (
            <>
              {recon.orphans.stripeOnly.length > 0 && (
                <div className="text-state-pending mt-1">
                  Stripe-only ({recon.orphans.stripeOnly.length}): {recon.orphans.stripeOnly.slice(0, 5).join(", ")}
                </div>
              )}
              {recon.orphans.ledgerOnly.length > 0 && (
                <div className="text-state-pending mt-1">
                  Ledger-only ({recon.orphans.ledgerOnly.length}): {recon.orphans.ledgerOnly.slice(0, 5).join(", ")}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-6">
          {error}
        </div>
      )}

      {items === null && !error && (
        <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">Loading…</p>
      )}
      {items && items.length === 0 && (
        <p className="font-serif text-sm text-ink-2">No payments yet.</p>
      )}
      {items && items.length > 0 && (
        <div className="border border-hairline overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-paper-2 text-[10px] font-mono uppercase tracking-widest text-ink-3">
              <tr className="border-b border-hairline">
                <th className="text-left px-4 py-3 font-bold">Date</th>
                <th className="text-left px-4 py-3 font-bold">Donor</th>
                <th className="text-left px-4 py-3 font-bold">Wish</th>
                <th className="text-right px-4 py-3 font-bold">Amount</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Stripe pi_</th>
                <th className="text-left px-4 py-3 font-bold">WC order</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline text-ink-2">
              {items.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-3 font-mono text-xs">
                    {new Date(p.createdAt).toLocaleDateString("en-GB")}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/donors/${encodeURIComponent(p.donorEmail)}`}
                      className="text-ink hover:text-claude underline decoration-hairline"
                    >
                      {p.donorEmail}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm">{p.metadata?.dpfWishId || "general"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-right">
                    US${p.amountUsd.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest">
                    {p.status}
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] text-ink-3">
                    {p.externalId.slice(0, 14)}…
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">#{p.invoiceId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

export function PaymentsAdmin() {
  return (
    <AdminGuard allow={["admin"]}>
      <PaymentsAdminInner />
    </AdminGuard>
  );
}
